// Topbar — also surfaces the notification bell with live pending-access-request count.
// On click, opens a drawer (rendered by the notifications component).
import { getSession, signOut } from '../auth';
import { getTenant } from '../theme';
import { TENANTS } from '../config';
import { mountNotificationsBadge, openNotificationsDrawer } from './notifications';

export function renderTopbar(theme: 'light' | 'dark') {
  const session = getSession();
  const tenantId = getTenant();
  const t = TENANTS[tenantId] || TENANTS.techtatwa;

  const roleChip = session
    ? `<span class="pill ${session.role === 'owner' ? 'pill-success' : 'pill-info'}">${session.role}</span>`
    : '';
  const devName = ''; // legacy slot, kept for parity
  const switchTenant = session
    ? '<span class="t-meta touch-target" data-action="switch-tenant" style="cursor:pointer; text-decoration:underline; text-underline-offset:3px;">↗ another tenant</span>'
    : '';
  const avatar = session ? `<button class="avatar av-c3 touch-target" data-action="goto" data-route="settings" title="${session.name} — open settings" style="cursor:pointer; border:0;">${session.initials}</button>` : '';

  // Notification bell is only visible to owner/admin. It is wired up after
  // mount via mountNotificationsBadge() in bindTopbarActions.
  const isAdmin = !!(session && (session.role === 'owner' || session.role === 'admin'));
  const bell = isAdmin
    ? `<button class="bell" id="notif-bell" type="button" data-action="open-notifications" aria-label="Notifications" style="position:relative;cursor:pointer;background:transparent;border:0;color:var(--ink);padding:6px 10px;border-radius:10px;font:inherit;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 16v-5a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2zM10 20a2 2 0 0 0 4 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span id="notif-badge" hidden style="position:absolute;top:0;right:0;background:#d97706;color:#fff;border-radius:999px;min-width:18px;height:18px;font-size:11px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;padding:0 5px;line-height:1;">0</span>
      </button>`
    : '';

  return `
    <header class="topbar" role="banner">
      <a class="brand" href="#/overview" aria-label="${t.nameLatin}">
        <span class="brand-rho" style="display:inline-flex; align-items:center; justify-content:center;">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" style="display:block;">
            <path d="M16 8.5C15.4 7.2 14.2 6.4 12.6 6.4c-2.8 0-4.9 2.1-4.9 4.9s2.1 4.9 4.9 4.9c1.5 0 2.8-.7 3.4-1.9" stroke="#fff" stroke-width="1.9" stroke-linecap="round"/>
          </svg></span>
        ${devName}
        <span class="brand-name">${t.nameLatin.toLowerCase()}</span>
      </a>
      ${roleChip}
      <span class="spacer"></span>
      ${bell}
      ${switchTenant}
      <label class="search">
        <svg class="ic" viewBox="0 0 20 20"><circle cx="9" cy="9" r="6"/><line x1="13.5" y1="13.5" x2="18" y2="18"/></svg>
        <input type="search" placeholder="Search students, courses, leads…" />
        <span class="kbd">⌘K</span>
      </label>
      <div class="theme-switch" role="group" aria-label="Theme">
        <button data-theme-btn="light" class="${theme === 'light' ? 'active' : ''}" data-action="set-theme" data-theme="light">Light</button>
        <button data-theme-btn="dark"  class="${theme === 'dark'  ? 'active' : ''}" data-action="set-theme" data-theme="dark">Dark</button>
      </div>
      ${avatar}
    </header>
  `;
}

export function bindTopbarActions() {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const action = t.closest('[data-action]') as HTMLElement | null;
    if (!action) return;
    const a = action.dataset.action;
    if (a === 'set-theme') {
      import('../theme').then((m) => m.setTheme(action.dataset.theme as 'light' | 'dark'));
    }
    if (a === 'switch-tenant') { signOut(); location.hash = '#/login'; }
    if (a === 'open-notifications') {
      const session = getSession();
      if (!session) return;
      openNotificationsDrawer(session.tenantId);
    }
  });
  // Wire up the live badge as soon as the owner signs in.
  const session = getSession();
  if (session && (session.role === 'owner' || session.role === 'admin')) {
    mountNotificationsBadge(session.tenantId);
  }
}
